import React from "react";
import { Avatar } from "primereact/avatar";

interface CircularProgressRingProps {
  progress?: number;
  size: number;
  strokeWidth?: number;
  image?: string | null;
  showCheckmark?: boolean;
  gradientStart?: string;
  gradientEnd?: string;
  backgroundColor?: string;
  isVerified?: boolean;
  /** Fill color for the unverified warning ("!") badge. Defaults to the gold warning color. */
  warningColor?: string;
  label?: string;
  /** When true, use the special level badge SVG instead of the default status badge. */
  showLevelBadge?: boolean;
  /** Text/number to render inside the level badge SVG. */
  levelLabel?: string | number;
}

export const CircularProgressRing = ({
  progress = 75,
  size = 200,
  strokeWidth = 8,
  image = null,
  showCheckmark = true,
  gradientStart = "#10b981",
  gradientEnd = "#34d399",
  backgroundColor = "#f3f4f6",
  isVerified = false,
  warningColor = "#CC8925",
  label,
  showLevelBadge = false,
  levelLabel,
}: CircularProgressRingProps) => {
  const [imageError, setImageError] = React.useState(false);
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  const uniqueId = React.useId();

  React.useEffect(() => {
    if (image) {
      setImageError(false);
    }
  }, [image]);

  // Calculate checkmark badge size
  const badgeSize = size * 0.22;

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          {/* Gradient definition */}
          <linearGradient id={`gradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradientStart} />
            <stop offset="100%" stopColor={gradientEnd} />
          </linearGradient>
        </defs>

        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />

        {/* Progress circle with gradient */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#gradient-${uniqueId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="butt"
          className="transition-all duration-500 ease-in-out"
        />
      </svg>

      {/* Inner content */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ padding: strokeWidth * 1.5 }}
      >
        <div className="relative w-full h-full rounded-full overflow-hidden bg-gray-100 shadow-inner">
          {image && !imageError ? (
            <img
              src={image}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center  text-4xl font-bold">
              <Avatar
                className="w-full h-full bg-transparent text-black dark:text-white conthrax-font"
                shape="circle"
                icon="pi pi-user"
                label={label?.toLocaleUpperCase()}
              />
            </div>
          )}
        </div>
      </div>

      {/* Checkmark / status badge with custom SVG */}
      {showCheckmark && (
        <div
          className="absolute flex items-center justify-center"
          style={{
            width: badgeSize * 1.2,
            height: badgeSize * 1.2,
            right: size * 0.01,
            bottom: size * 0.08,
            overflow: "visible",
          }}
        >
          {showLevelBadge && levelLabel !== undefined ? (
            // Dedicated level badge SVG (admin level), separate from the default status badge.
            <svg
              width={badgeSize * 1.2}
              height={badgeSize * 1.32}
              viewBox="0 0 31 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.2109 32.4941L29.5952 24.5775V8.74414L15.2109 0.827475L0.82666 8.74414V24.5775L15.2109 32.4941Z"
                fill="#E5EDF6"
              />
              <path
                d="M15.2109 32.4941L15.6096 33.2185C15.3614 33.3551 15.0605 33.3551 14.8123 33.2185L15.2109 32.4941ZM29.5952 24.5775H30.422C30.422 24.8789 30.258 25.1565 29.9939 25.3018L29.5952 24.5775ZM29.5952 8.74414L29.9939 8.01978C30.258 8.16512 30.422 8.44269 30.422 8.74414H29.5952ZM15.2109 0.827475L14.8123 0.103107C15.0605 -0.0335045 15.3614 -0.0335045 15.6096 0.103107L15.2109 0.827475ZM0.82666 8.74414H-0.000166893C-0.000166893 8.44269 0.163895 8.16512 0.427991 8.01978L0.82666 8.74414ZM0.82666 24.5775L0.427991 25.3018C0.163895 25.1565 -0.000166893 24.8789 -0.000166893 24.5775H0.82666ZM15.2109 32.4941L14.8123 31.7698L29.1965 23.8531L29.5952 24.5775L29.9939 25.3018L15.6096 33.2185L15.2109 32.4941ZM29.5952 24.5775H28.7684V8.74414H29.5952H30.422V24.5775H29.5952ZM29.5952 8.74414L29.1965 9.46851L14.8123 1.55184L15.2109 0.827475L15.6096 0.103107L29.9939 8.01978L29.5952 8.74414ZM15.2109 0.827475L15.6096 1.55184L1.22533 9.46851L0.82666 8.74414L0.427991 8.01978L14.8123 0.103107L15.2109 0.827475ZM0.82666 8.74414H1.65349V24.5775H0.82666H-0.000166893V8.74414H0.82666ZM0.82666 24.5775L1.22533 23.8531L15.6096 31.7698L15.2109 32.4941L14.8123 33.2185L0.427991 25.3018L0.82666 24.5775Z"
                fill="url(#level-badge-border)"
              />
              <defs>
                <radialGradient
                  id="level-badge-border"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(15.2 16.7) scale(20 20)"
                >
                  <stop offset="0" stopColor="#002851" />
                  <stop offset="1" stopColor="#005AB7" />
                </radialGradient>
              </defs>
              <text
                x="50%"
                y="56%"
                textAnchor="middle"
                fill="#002851"
                dominantBaseline="middle"
                style={{
                  fontFamily:
                    "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 700,
                  fontStyle: "normal",
                  fontSize: 19.74,
                  lineHeight: "100%",
                  letterSpacing: "0",
                }}
              >
                {levelLabel}
              </text>
            </svg>
          ) : !isVerified ? (
            // Original warning badge (exclamation), unchanged.
            <svg
              width={badgeSize * 1.2}
              height={badgeSize * 1.2}
              viewBox="-2 -2 36 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ overflow: "visible" }}
            >
              <g transform="translate(16, 14) rotate(90) scale(1.5)">
                <path
                  d="M0 -12 L10.4 -6 L10.4 6 L0 12 L-10.4 6 L-10.4 -6 Z"
                  fill="#F3F2FF"
                  stroke="#E0DDFB"
                  strokeWidth="1.5"
                />
              </g>
              <path
                d="M14.5354 15.3856L14.5354 6.62004L16.9185 6.62004L16.9185 15.3856C16.9185 15.9252 16.3847 16.3623 15.727 16.3623C15.0693 16.3623 14.5354 15.9252 14.5354 15.3856Z"
                fill={warningColor}
              />
              <path
                d="M14.1739 20.4819L14.1739 18.6885L15.7271 17.7921L17.2811 18.6885L17.2811 20.4819L15.7271 21.3799L14.1739 20.4819Z"
                fill={warningColor}
              />
              <path
                d="M10.272 26.0319L10.2655 26.0319L10.1859 26.0302C9.20595 26.022 8.28667 25.4914 7.78807 24.6452L2.35766 15.4277C1.83857 14.547 1.83857 13.4522 2.35766 12.5706L7.78726 3.35321C8.29159 2.49789 9.22071 1.9665 10.2122 1.9665L21.2419 1.9665C22.2342 1.9665 23.1633 2.49789 23.6668 3.35239L25.9507 7.2288L29.0972 12.5698C29.6163 13.4506 29.6163 14.5453 29.0972 15.4269L23.6668 24.6452C23.1633 25.5005 22.2334 26.0319 21.2419 26.0319L10.272 26.0319ZM10.213 28L21.2419 28C22.9362 28 24.5033 27.1037 25.3627 25.6448L30.7931 16.4274C31.6755 14.9291 31.6755 13.0709 30.7931 11.5726L27.6466 6.23161L25.3627 2.3552C24.5033 0.896324 22.9362 2.00514e-06 21.2419 1.85703e-06L10.213 8.92846e-07C8.51875 7.44732e-07 6.95162 0.896323 6.0922 2.3552L0.661788 11.5726C-0.220593 13.0709 -0.220593 14.9299 0.661788 16.4274L6.0922 25.6448C6.94506 27.0914 8.49168 27.9852 10.1695 27.9984C10.1843 28 10.1982 28 10.213 28Z"
                fill={warningColor}
              />
            </svg>
          ) : (
            // Original verified badge (green check), unchanged.
            <svg
              width={badgeSize * 1.2}
              height={badgeSize * 1.2}
              viewBox="0 0 33 33"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.3157 0.465394C15.8145 0.177407 16.429 0.177407 16.9278 0.465395L29.2775 7.59548C29.7763 7.88346 30.0836 8.41568 30.0836 8.99166V23.2518C30.0836 23.8278 29.7763 24.36 29.2775 24.648L16.9278 31.7781C16.429 32.0661 15.8145 32.0661 15.3157 31.7781L2.96599 24.648C2.46718 24.36 2.1599 23.8278 2.1599 23.2518V8.99166C2.1599 8.41568 2.46718 7.88346 2.96599 7.59547L15.3157 0.465394Z"
                fill="#E6EEF7"
              />
              <path
                d="M15.4053 29.0174C14.6254 29.0174 13.8456 28.8109 13.1503 28.3972L6.12928 24.2229C4.77947 23.4197 3.94092 21.9397 3.94092 20.3588V11.8805C3.94092 10.3002 4.77947 8.81956 6.12862 8.017L13.1503 3.84274C14.5415 3.01596 16.2699 3.01596 17.6604 3.84274L22.8983 6.95678C23.2789 7.18282 23.4056 7.67794 23.1816 8.06207C22.9576 8.44619 22.467 8.57401 22.0864 8.34798L16.8485 5.23393C15.9579 4.7045 14.8521 4.7045 13.9622 5.23393L6.94117 9.40819C6.07729 9.92148 5.5407 10.8693 5.5407 11.8805V20.3588C5.5407 21.3699 6.07729 22.3178 6.94117 22.8311L13.9622 27.0053C14.8521 27.5348 15.9586 27.5348 16.8485 27.0053L23.8695 22.8311C24.7247 22.3225 25.2607 21.3854 25.2687 20.3857V20.346L25.2693 11.5878C25.2693 11.1418 25.6273 10.7805 26.0692 10.7805C26.5111 10.7805 26.8691 11.1418 26.8691 11.5878V20.3588C26.8691 20.3797 26.8691 20.3998 26.8678 20.42C26.8478 21.9754 26.0119 23.4311 24.6814 24.2223L17.6597 28.3965C16.9645 28.8102 16.1852 29.0174 15.4053 29.0174Z"
                fill="#22C55E"
              />
              <path
                d="M25.9569 7.02732C25.737 7.06364 24.0332 8.62706 24.0332 8.62706C20.8483 11.2527 17.8094 14.0479 14.9831 17.059C14.8238 17.2285 14.5952 17.5514 14.4392 17.6813C14.4032 17.7115 14.3972 17.7337 14.3392 17.7135C13.5487 16.2685 12.6248 14.8558 11.6116 13.5514C11.3183 13.174 10.4038 11.9853 10.0431 11.8144C9.20659 11.4175 8.36937 12.36 8.75998 13.172C8.98795 13.6463 9.70452 14.4259 10.0438 14.9258C11.037 16.3916 11.9189 18.0149 12.7014 19.6032C13.0134 20.2356 13.3647 21.2554 13.7186 21.7977C13.9346 22.128 14.4012 22.2127 14.7352 22.0123C15.4471 21.1532 16.0603 20.2026 16.7302 19.3052C18.8646 16.4441 21.2689 13.6732 23.7433 11.1027C24.6065 10.2053 25.7836 9.23655 26.5502 8.31828C26.6142 8.24159 26.7208 8.12588 26.7562 8.03775C26.9788 7.47333 26.5609 6.92843 25.9569 7.02732Z"
                fill="#22C55E"
              />
            </svg>
          )}
        </div>
      )}
    </div>
  );
};
